import { useState, useEffect } from 'react';
import db from '@/db';
import { formatCurrency } from '@/lib/formatters';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Pencil, Trash2, Search, Package, Barcode } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['Cerveja', 'Refrigerante', 'Agua', 'Suco', 'Energetico', 'Destilado', 'Vinho', 'Outro'];

export default function StockPage() {
  const [beverages, setBeverages] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', price: '', stock: '', category: 'Cerveja', barcode: '' });

  const loadBeverages = async () => {
    const all = await db.beverages.toArray();
    setBeverages(all);
  };

  useEffect(() => {
    loadBeverages();
  }, []);

  const filtered = beverages.filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase()) ||
    b.category.toLowerCase().includes(search.toLowerCase())
  );

  const openAddForm = () => {
    setEditing(null);
    setForm({ name: '', price: '', stock: '', category: 'Cerveja', barcode: '' });
    setShowForm(true);
  };

  const openEditForm = (bev) => {
    setEditing(bev);
    setForm({
      name: bev.name,
      price: String(bev.price),
      stock: String(bev.stock),
      category: bev.category,
      barcode: bev.barcode || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.price || !form.stock) {
      toast.error('Preencha todos os campos');
      return;
    }

    const bc = form.barcode.trim().replace(/\D/g, '');
    if (bc && bc.length !== 13) {
      toast.error('Código de barras deve ter 13 dígitos (EAN-13) ou ficar vazio');
      return;
    }

    const data = {
      name: form.name.trim(),
      price: parseFloat(form.price),
      stock: parseInt(form.stock, 10),
      category: form.category,
      barcode: bc || null,
    };

    // Verifica duplicata
    if (bc) {
      const dup = beverages.find((b) => String(b.barcode || '').replace(/\D/g, '') === bc && b.id !== editing?.id);
      if (dup) {
        toast.error(`Código já está em "${dup.name}"`);
        return;
      }
    }

    if (editing) {
      await db.beverages.update(editing.id, data);
      toast.success('Bebida atualizada');
    } else {
      await db.beverages.add(data);
      toast.success('Bebida adicionada');
    }

    setShowForm(false);
    loadBeverages();
  };

  const handleDelete = async (bev) => {
    await db.beverages.delete(bev.id);
    toast.success(`${bev.name} removido`);
    loadBeverages();
  };

  return (
    <div data-testid="stock-page" className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-3xl tracking-tight font-bold text-zinc-900">
            Estoque de Bebidas
          </h2>
          <p className="text-sm text-zinc-500 mt-1">{beverages.length} itens cadastrados</p>
        </div>
        <button
          data-testid="add-beverage-btn"
          onClick={openAddForm}
          className="flex items-center gap-2 bg-[#2563eb] text-white rounded-lg font-bold px-6 py-3 border-2 border-transparent hover:bg-[#1d4ed8] active:translate-y-[2px] transition-all duration-150 min-h-[48px]"
        >
          <Plus size={20} />
          <span>Nova Bebida</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
        <Input
          data-testid="stock-search"
          placeholder="Buscar bebida ou categoria..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-12 border-2 border-zinc-200 rounded-xl text-base"
        />
      </div>

      {/* Beverage Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map((bev) => (
          <div
            key={bev.id}
            data-testid={`stock-item-${bev.id}`}
            className="bg-white border-2 border-zinc-200 rounded-xl p-5 hover:border-zinc-400 transition-all duration-150"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-zinc-900 truncate">{bev.name}</p>
                <Badge variant="outline" className="mt-1 text-xs">
                  {bev.category}
                </Badge>
              </div>
              <div className="flex gap-1 ml-2">
                <button
                  data-testid={`edit-beverage-${bev.id}`}
                  onClick={() => openEditForm(bev)}
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-all"
                >
                  <Pencil size={14} />
                </button>
                <button
                  data-testid={`delete-beverage-${bev.id}`}
                  onClick={() => handleDelete(bev)}
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-all"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="mt-3 flex items-end justify-between">
              <p className="font-mono text-xl font-black text-zinc-900 tracking-tighter">
                {formatCurrency(bev.price)}
              </p>
              <div className="text-right">
                <p className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-400">
                  Estoque
                </p>
                <p
                  className={`font-mono text-lg font-bold ${
                    bev.stock <= 5 ? 'text-red-500' : 'text-zinc-900'
                  }`}
                >
                  {bev.stock}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-20 text-zinc-400">
          <Package size={48} className="mx-auto mb-4 opacity-30" />
          <p className="text-lg font-semibold">Nenhuma bebida encontrada</p>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md bg-white border-2 border-zinc-900 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {editing ? 'Editar Bebida' : 'Nova Bebida'}
            </DialogTitle>
            <DialogDescription>
              {editing ? 'Atualize os dados da bebida' : 'Cadastre uma nova bebida no estoque'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">
                Nome
              </label>
              <Input
                data-testid="beverage-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Cerveja Lata 350ml"
                className="h-12 border-2 border-zinc-200 rounded-xl text-base"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">
                  Preco (R$)
                </label>
                <Input
                  data-testid="beverage-price-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="0.00"
                  className="h-12 border-2 border-zinc-200 rounded-xl text-base font-mono"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">
                  Estoque
                </label>
                <Input
                  data-testid="beverage-stock-input"
                  type="number"
                  min="0"
                  value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: e.target.value })}
                  placeholder="0"
                  className="h-12 border-2 border-zinc-200 rounded-xl text-base font-mono"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">
                Categoria
              </label>
              <Select
                value={form.category}
                onValueChange={(val) => setForm({ ...form, category: val })}
              >
                <SelectTrigger data-testid="beverage-category-select" className="h-12 border-2 border-zinc-200 rounded-xl text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Fase 33 — Código de barras EAN-13 */}
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block flex items-center gap-1.5">
                <Barcode size={12} /> Código de barras (EAN-13)
              </label>
              <Input
                data-testid="beverage-barcode-input"
                value={form.barcode}
                onChange={(e) => setForm({ ...form, barcode: e.target.value.replace(/\D/g, '').slice(0, 13) })}
                placeholder="Bipe aqui ou digite os 13 dígitos"
                autoComplete="off"
                className="h-11 border-2 border-zinc-200 rounded-xl font-mono tracking-wider"
              />
              <p className="text-[10px] text-zinc-400 mt-1">
                Opcional. Bipar este código no Caixa adiciona o produto direto na comanda.
              </p>
            </div>
            <button
              data-testid="save-beverage-btn"
              onClick={handleSave}
              className="w-full bg-[#2563eb] text-white rounded-lg font-bold border-2 border-transparent hover:bg-[#1d4ed8] active:translate-y-[2px] transition-all py-4 mt-2 min-h-[48px]"
            >
              {editing ? 'Atualizar' : 'Cadastrar'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
